import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Repeat, Plus, Pause, Play, Square, Pencil, ListChecks, X, AlertTriangle } from "lucide-react";
import { api, formatApiError, REC_STATUS_LABELS, REC_STATUS_STYLE, REC_OVERLAP_LABELS } from "@/lib/api";
import { toast } from "sonner";
import RecurrenceModal from "@/components/RecurrenceModal";

export default function RecurrencesPage() {
  const ctx = useOutletContext();
  const { projectId, project } = ctx;
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // { recurrenceId } | { recurrenceId: null }
  const [occ, setOcc] = useState(null); // { rec, tasks }
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/recurrences`); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, ctx.refreshKey]);

  const canAdmin = data?.can_admin;
  const recs = data?.recurrences || [];

  const act = async (rid, action, body) => {
    setBusy(rid + action);
    try {
      await api.post(`/recurrences/${rid}/${action}`, body || {});
      toast.success("Atualizado.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const openOccurrences = async (rec) => {
    try { const { data } = await api.get(`/recurrences/${rec.id}/occurrences`); setOcc({ rec, tasks: data }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  return (
    <div data-testid="recurrences-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{project.workspace_name}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Recorrências</h1>
          <p className="text-slate-500 mt-1">Modelos que geram tarefas comuns no Backlog conforme a programação. O modelo não aparece no Backlog, Kanban, Sprints ou métricas.</p>
        </div>
        {canAdmin && project.status === "active" && project.project_status !== "completed" && (
          <button onClick={() => setModal({ recurrenceId: null })} data-testid="recurrence-create-btn" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"><Plus className="w-4 h-4" /> Nova recorrência</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="recurrences-section">
        {data === null ? (
          <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>
        ) : recs.length === 0 ? (
          <div className="p-12 text-center" data-testid="recurrences-empty">
            <Repeat className="w-10 h-10 text-slate-300 mx-auto" strokeWidth={1.5} />
            <h3 className="font-heading text-lg font-semibold mt-3">Nenhuma recorrência</h3>
            <p className="text-sm text-slate-500 mt-1">Crie um modelo para gerar tarefas automaticamente conforme uma frequência.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="recurrences-list">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Modelo</th>
                <th className="px-2 py-3 w-44">Frequência</th>
                <th className="px-2 py-3 w-36">Responsável</th>
                <th className="px-2 py-3 w-28">Próxima</th>
                <th className="px-2 py-3 w-40">Sobreposição</th>
                <th className="px-2 py-3 w-32">Status</th>
                <th className="px-2 py-3 w-20">Geradas</th>
                <th className="px-2 py-3 w-44"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recs.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`recurrence-row-${r.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      <Repeat className="w-3.5 h-3.5 text-slate-400" /> {r.title}
                      {r.status === "needs_review" && <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />}
                    </div>
                    <div className="text-xs text-slate-400">Última: {fmt(r.last_occurrence_at)}</div>
                  </td>
                  <td className="px-2 py-3 text-slate-600">{r.frequency_summary}</td>
                  <td className="px-2 py-3 text-slate-600">{r.assignee_name || "—"}</td>
                  <td className="px-2 py-3 text-slate-600">{fmt(r.next_run_date)}</td>
                  <td className="px-2 py-3 text-slate-500 text-xs">{REC_OVERLAP_LABELS[r.overlap_policy]}</td>
                  <td className="px-2 py-3"><span className={`text-xs px-2 py-0.5 rounded-md ${REC_STATUS_STYLE[r.status]}`} data-testid={`recurrence-status-${r.id}`}>{REC_STATUS_LABELS[r.status]}</span></td>
                  <td className="px-2 py-3 text-slate-600">
                    <button onClick={() => openOccurrences(r)} data-testid={`recurrence-occurrences-${r.id}`} className="inline-flex items-center gap-1 hover:underline">
                      <ListChecks className="w-3.5 h-3.5" /> {r.generated_count}
                    </button>
                  </td>
                  <td className="px-2 py-3 text-right">
                    {canAdmin && (
                      <div className="inline-flex items-center gap-1">
                        {(r.status === "active" || r.status === "paused" || r.status === "needs_review") && (
                          <button onClick={() => setModal({ recurrenceId: r.id })} data-testid={`recurrence-edit-${r.id}`} title="Editar" className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                        {(r.status === "active" || r.status === "needs_review") && (
                          <button onClick={() => act(r.id, "pause")} disabled={busy === r.id + "pause"} data-testid={`recurrence-pause-${r.id}`} title="Pausar" className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"><Pause className="w-3.5 h-3.5" /></button>
                        )}
                        {r.status === "paused" && (
                          <button onClick={() => act(r.id, "resume")} disabled={busy === r.id + "resume"} data-testid={`recurrence-resume-${r.id}`} title="Retomar" className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"><Play className="w-3.5 h-3.5" /></button>
                        )}
                        {r.status !== "ended" && (
                          <button onClick={() => { if (window.confirm("Encerrar esta recorrência? Não poderá ser retomada.")) act(r.id, "end"); }} disabled={busy === r.id + "end"} data-testid={`recurrence-end-${r.id}`} title="Encerrar" className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-rose-600"><Square className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <RecurrenceModal projectId={projectId} project={project} recurrenceId={modal.recurrenceId}
          onClose={() => setModal(null)} onSaved={() => { load(); ctx.bump?.(); }} />
      )}

      {occ && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={() => setOcc(null)} data-testid="recurrence-occurrences-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight">Ocorrências geradas — {occ.rec.title}</h2>
              <button onClick={() => setOcc(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {occ.tasks.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Nenhuma ocorrência gerada ainda.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {occ.tasks.map((t) => (
                    <li key={t.id} className="py-3 flex items-center gap-3" data-testid={`occurrence-${t.id}`}>
                      <button onClick={() => { ctx.openTask?.(t.id); setOcc(null); }} className="text-sm font-medium text-slate-900 hover:underline text-left flex-1 min-w-0 truncate">
                        #{t.recurrence_seq} · {t.title}
                      </button>
                      {t.assignment_needed && <span className="text-xs px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 shrink-0">Atribuição necessária</span>}
                      <span className="text-xs text-slate-400 shrink-0">{t.recurrence_scheduled_date}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
